// ============================================================================
// PairsForm — Hook permissions (Sprint 8 · Étape B1)
// ============================================================================
// Charge les permissions courantes de l'utilisateur et expose un hook
// simple `useCan(key)`. Le résultat est mis en cache par un PermissionsProvider
// qui fait un seul GET /api/permissions/me à la connexion et le rafraîchit
// quand l'utilisateur change (login / logout / switch d'organisation).
//
// Dans cette étape B1, le provider n'est PAS intégré dans l'arbre React —
// il est prêt à l'emploi mais non monté. On l'activera à l'Étape B2 quand
// la matrice sera seedée et qu'au moins une UI consommera les permissions.
// ============================================================================

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * Une « casquette » = les permissions de la personne DANS UNE de ses
 * organisations. Un alumni de Cecodev devenu animateur de Cecodev_Inter en a
 * deux. Sert à la VISIBILITÉ du menu personnel et à la bascule implicite
 * (FX 2026-08-19) ; les gestes, eux, restent gouvernés par `allowed`, calculé
 * pour l'org active — le serveur ne connaît que celle du JWT (P8).
 */
export interface Casquette {
  membershipId: string;
  organisationId: string;
  organisationNom: string | null;
  organisationType: string;
  roles: string[];
  permissions: ReadonlySet<string>;
}

interface PermissionsContextValue {
  /** Permissions autorisées pour l'utilisateur courant (set de clés). */
  allowed: ReadonlySet<string>;
  /** Une entrée par membership, permissions calculées pour SON org. */
  casquettes: Casquette[];
  /** True tant que le fetch initial n'est pas terminé. */
  loading: boolean;
  /** Force un re-fetch — à appeler après modification de la matrice. */
  refresh: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue>({
  allowed: new Set(),
  casquettes: [],
  loading: true,
  refresh: async () => {},
});

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [allowed, setAllowed] = useState<Set<string>>(new Set());
  const [casquettes, setCasquettes] = useState<Casquette[]>([]);
  const [loading, setLoading] = useState(true);

  interface CasquetteDTO {
    membershipId: string;
    organisationId: string;
    organisationNom: string | null;
    organisationType: string;
    roles: string[];
    permissions: string[];
  }

  async function fetchPermissions() {
    if (!user) {
      setAllowed(new Set());
      setCasquettes([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await api.get<{ permissions: string[]; casquettes?: CasquetteDTO[] }>("/permissions/me");
      setAllowed(new Set(res.data?.permissions ?? []));
      setCasquettes(
        (res.data?.casquettes ?? []).map((c) => ({ ...c, permissions: new Set(c.permissions) })),
      );
    } catch {
      setAllowed(new Set());
      setCasquettes([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.organisationId]);

  const value = useMemo<PermissionsContextValue>(
    () => ({ allowed, casquettes, loading, refresh: fetchPermissions }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowed, casquettes, loading],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

/**
 * Retourne true si l'utilisateur courant a la permission.
 * Aucun fallback réseau : le Provider charge tout au mount.
 * Pendant le chargement initial, renvoie false (refus par défaut) — les
 * composants appelants peuvent lire `loading` si besoin de distinguer
 * "interdit" vs "pas encore su".
 */
export function useCan(key: string): boolean {
  const ctx = useContext(PermissionsContext);
  return ctx.allowed.has(key);
}

/**
 * Variante qui expose loading et refresh — utile pour les pages admin qui
 * modifient la matrice.
 */
export function usePermissions(): PermissionsContextValue {
  return useContext(PermissionsContext);
}
